import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Globe, Search, ArrowUpRight, ArrowDownRight, ExternalLink, Sparkles,
  TrendingUp, MapPin, BarChart3, Link2, Bot, Lightbulb, RefreshCw, Trophy, AlertTriangle,
  Facebook, Instagram, Heart, MousePointerClick, Eye, Users, MessageCircle,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/dateFormat";
import AnalyticsAddTaskButton from "@/components/AnalyticsAddTaskButton";

/* ────────────────────────────────────────────────────────────────────────── */
/* MOCK DATA — used as fallback when GA4 / Search Console aren't configured.   */
/* When the live wires return data, the live values override these constants.  */
/* ────────────────────────────────────────────────────────────────────────── */

function fmtDur(seconds) {
  const s = Math.max(0, Math.round(Number(seconds || 0)));
  const m = Math.floor(s / 60);
  return `${m}m ${String(s - m * 60).padStart(2, "0")}s`;
}

const MOCK_KPIS = [
  { id: "visitors", label: "Visitors", value: 12480, delta: +14.2, sparkline: [80, 95, 110, 102, 130, 145, 160, 158, 172, 188, 195, 210] },
  { id: "pageviews", label: "Page views", value: 38214, delta: +9.6, sparkline: [220, 240, 260, 280, 270, 295, 310, 305, 330, 348, 360, 372] },
  { id: "session", label: "Avg session", value: "2m 48s", delta: +3.1, sparkline: [140, 150, 160, 158, 162, 165, 170, 172, 168, 175, 180, 188] },
  { id: "bounce", label: "Bounce rate", value: "41%", delta: -5.2, sparkline: [55, 53, 52, 50, 48, 47, 45, 44, 43, 42, 41, 41] },
];

const MOCK_TIMESERIES = Array.from({ length: 30 }, (_, i) => {
  const seed = Math.sin(i * 0.42) * 80 + 240;
  const noise = ((i * 9301 + 49297) % 233280) / 233280;
  return {
    day: `Day ${i + 1}`,
    visitors: Math.round(seed + noise * 60),
    pageviews: Math.round((seed + noise * 60) * 3.1),
  };
});

const MOCK_SOURCES = [
  { name: "Organic search", value: 5210, color: "#F54800" },
  { name: "Direct", value: 3120, color: "#0F172A" },
  { name: "LinkedIn", value: 1840, color: "#0A66C2" },
  { name: "Referral", value: 1120, color: "#16A34A" },
  { name: "Email", value: 720, color: "#9333EA" },
  { name: "Twitter / X", value: 470, color: "#0EA5E9" },
];

const PAGE_STATUS = (views) => {
  if (views >= 2000) return { tone: "green", label: "Top performing" };
  if (views >= 600) return { tone: "yellow", label: "Performing average" };
  return { tone: "red", label: "Underperforming" };
};

const MOCK_TOP_PAGES = [
  { path: "/", title: "Homepage — Analogy Design", views: 4218, avg_time: "3m 12s", bounce: "32%" },
  { path: "/work/canopy-energy", title: "Case study: Canopy Energy", views: 2856, avg_time: "4m 02s", bounce: "28%" },
  { path: "/services/mvp", title: "Service: MVP development", views: 2401, avg_time: "3m 48s", bounce: "30%" },
  { path: "/blog/why-hardware-startups-need-dfm", title: "Why hardware startups need DFM", views: 2186, avg_time: "5m 18s", bounce: "22%" },
  { path: "/about", title: "About Analogy", views: 1942, avg_time: "2m 30s", bounce: "44%" },
  { path: "/services/concept-design", title: "Service: Concept design", views: 1718, avg_time: "3m 21s", bounce: "37%" },
  { path: "/blog/launching-a-product-in-90-days", title: "Launching a product in 90 days", views: 1480, avg_time: "4m 50s", bounce: "26%" },
  { path: "/careers", title: "Careers — Join Analogy", views: 1208, avg_time: "2m 02s", bounce: "55%" },
  { path: "/services/launch", title: "Service: Launch sprints", views: 982, avg_time: "3m 12s", bounce: "39%" },
  { path: "/contact", title: "Contact us", views: 864, avg_time: "1m 58s", bounce: "48%" },
  { path: "/work/lumen-medical", title: "Case study: Lumen Medical", views: 740, avg_time: "3m 44s", bounce: "31%" },
  { path: "/work/nimble-robotics", title: "Case study: Nimble Robotics", views: 612, avg_time: "3m 30s", bounce: "34%" },
  { path: "/blog/design-for-manufacturing-pitfalls", title: "DFM pitfalls to avoid", views: 540, avg_time: "4m 08s", bounce: "29%" },
  { path: "/process", title: "Our process", views: 472, avg_time: "2m 22s", bounce: "46%" },
  { path: "/blog/picking-the-right-co-manufacturer", title: "Picking the right co-manufacturer", views: 380, avg_time: "5m 02s", bounce: "24%" },
  { path: "/work/sunburst-mobility", title: "Case study: Sunburst Mobility", views: 318, avg_time: "3m 14s", bounce: "33%" },
  { path: "/services/dfm", title: "Service: DFM", views: 264, avg_time: "2m 48s", bounce: "42%" },
  { path: "/blog/ai-in-hardware-design", title: "AI in hardware design", views: 226, avg_time: "3m 32s", bounce: "35%" },
  { path: "/legal/privacy", title: "Privacy policy", views: 192, avg_time: "1m 02s", bounce: "65%" },
  { path: "/legal/terms", title: "Terms of service", views: 148, avg_time: "0m 55s", bounce: "70%" },
];

const MOCK_TOP_QUERIES = [
  { q: "product design studio for hardware startups", impressions: 4820, clicks: 312, position: 3.2 },
  { q: "design for manufacturing consultant india", impressions: 3940, clicks: 248, position: 4.1 },
  { q: "mvp development for hardware founders", impressions: 3210, clicks: 198, position: 5.4 },
  { q: "concept design studio bangalore", impressions: 2680, clicks: 184, position: 2.8 },
  { q: "industrial design consultancy b2b", impressions: 2120, clicks: 142, position: 6.2 },
  { q: "how to launch a hardware product", impressions: 1980, clicks: 96, position: 7.8 },
  { q: "analogy design", impressions: 1740, clicks: 1080, position: 1.1 },
  { q: "dfm partner mumbai", impressions: 1520, clicks: 88, position: 4.6 },
  { q: "hardware mvp india", impressions: 1380, clicks: 72, position: 5.9 },
  { q: "product design studio for startups", impressions: 1240, clicks: 80, position: 6.5 },
  { q: "industrial designer for consumer electronics", impressions: 1180, clicks: 64, position: 7.2 },
  { q: "rapid prototyping india", impressions: 1080, clicks: 52, position: 8.4 },
  { q: "design firm for d2c brands", impressions: 980, clicks: 44, position: 9.1 },
  { q: "concept to production design partner", impressions: 920, clicks: 38, position: 9.6 },
  { q: "product launch consultant b2b", impressions: 860, clicks: 32, position: 10.2 },
  { q: "hardware design sprints", impressions: 780, clicks: 28, position: 10.8 },
  { q: "iot product design studio", impressions: 720, clicks: 24, position: 11.4 },
  { q: "robotics product design", impressions: 640, clicks: 20, position: 12.1 },
  { q: "medical device design partner", impressions: 580, clicks: 18, position: 12.6 },
  { q: "industrial design portfolio review", impressions: 520, clicks: 14, position: 13.5 },
];

const MOCK_GEO = [
  { code: "IN", country: "India", flag: "🇮🇳", visitors: 5820 },
  { code: "US", country: "United States", flag: "🇺🇸", visitors: 2410 },
  { code: "GB", country: "United Kingdom", flag: "🇬🇧", visitors: 980 },
  { code: "DE", country: "Germany", flag: "🇩🇪", visitors: 620 },
  { code: "AE", country: "UAE", flag: "🇦🇪", visitors: 540 },
  { code: "SG", country: "Singapore", flag: "🇸🇬", visitors: 480 },
  { code: "AU", country: "Australia", flag: "🇦🇺", visitors: 410 },
  { code: "CA", country: "Canada", flag: "🇨🇦", visitors: 350 },
  { code: "NL", country: "Netherlands", flag: "🇳🇱", visitors: 280 },
  { code: "FR", country: "France", flag: "🇫🇷", visitors: 240 },
];

const MOCK_BACKLINKS = {
  total: 184,
  quality: 72, // out of 100
  recent: [
    { domain: "techcrunch.com",  url: "https://techcrunch.com/2026/01/18/analogy-dfm-playbook/", da: 92, anchor: "Analogy's design-for-manufacturing playbook", date: "2 weeks ago" },
    { domain: "yourstory.com",   url: "https://yourstory.com/2026/01/top-10-product-design-studios-india", da: 84, anchor: "Top 10 product design studios in India", date: "1 month ago" },
    { domain: "designboom.com",  url: "https://www.designboom.com/2026/01/lumen-medical-if-design-award/", da: 88, anchor: "Lumen Medical wins iF Award", date: "1 month ago" },
    { domain: "the-ken.com",     url: "https://the-ken.com/story/hardware-startups-design-partners/", da: 76, anchor: "How hardware startups should pick design partners", date: "5 weeks ago" },
    { domain: "indiehackers.com",url: "https://www.indiehackers.com/post/bootstrapping-design-consultancy-analogy", da: 70, anchor: "Bootstrapping a design consultancy", date: "2 months ago" },
  ],
};

const MOCK_COMPETITORS = [
  { name: "frog (frogdesign.com)", visitors: 142000, backlinks: 8400, top_keyword: "product design firm", position: 1.4 },
  { name: "Lounge Lizard", visitors: 88000, backlinks: 3120, top_keyword: "design agency new york", position: 2.1 },
  { name: "Studio Banana", visitors: 54000, backlinks: 2280, top_keyword: "industrial design studio", position: 2.8 },
  { name: "Analogy Design (you)", visitors: 12480, backlinks: 184, top_keyword: "product design studio for hardware startups", position: 3.2, you: true },
];

// AI-style recommendations — would be generated by gpt-5-mini once data is live
const MOCK_AI_INSIGHTS = [
  {
    title: "Double down on DFM content",
    severity: "high",
    body: "Your top blog post 'Why hardware startups need DFM' is converting at 5m 18s avg time and 22% bounce — way above your site avg. Search Console shows 4 of your top 20 queries are DFM-related but you only have 2 dedicated DFM posts. Spin up 3 more (DFM checklist, DFM cost calculator, DFM for medical devices) to capture this demand.",
    icon: TrendingUp,
  },
  {
    title: "Fix your bounce rate on /careers and /contact",
    severity: "medium",
    body: "/careers (55%) and /contact (48%) bounce rates are 14-20 points higher than your homepage. Add inline social proof (recent hires, client logos) above the fold on /careers and make /contact's primary CTA a calendar booking, not a form.",
    icon: Lightbulb,
  },
  {
    title: "You're #3-#5 on 12 of your 20 top queries",
    severity: "high",
    body: "Industry benchmark: moving from position #4 to #1 typically 4-6x's organic clicks for the same query. Pick 'design for manufacturing consultant india' (pos 4.1, 3940 imp/mo) and build 1 cornerstone page + 3 supporting articles — this single move could add ~2400 monthly clicks within 90 days.",
    icon: Trophy,
  },
  {
    title: "AI-engine optimization (AEO) is your biggest unmoated channel",
    severity: "medium",
    body: "0 of your pages are currently cited by ChatGPT, Perplexity, or Claude when people ask 'product design studio for hardware startups in India'. Frog and Lounge Lizard are. Add a 200-word FAQ section to your top 5 pages with question-first headers — AI engines crawl those preferentially. Free win.",
    icon: Bot,
  },
  {
    title: "You're missing on local intent",
    severity: "low",
    body: "Google Business Profile shows you have 4 reviews vs frog's 87. Local 'design studio bangalore' searches drive 14% of your traffic — getting to 30+ GMB reviews could double that. Reach out to 10 happy clients this week.",
    icon: MapPin,
  },
];

/* ────────────────────────────────────────────────────────────────────────── */
function Spark({ data, color = "#F54800" }) {
  const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - (v / Math.max(...data)) * 100}`).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-8" aria-hidden="true">
      <polyline fill="none" stroke={color} strokeWidth="2" points={points} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function KPICard({ kpi }) {
  const hasDelta = kpi.delta != null && Number.isFinite(kpi.delta);
  const up = (kpi.delta || 0) >= 0;
  return (
    <div className="card-flat p-4" data-testid={`analytics-kpi-${kpi.id}`}>
      <div className="label-overline">{kpi.label}</div>
      <div className="flex items-end justify-between mt-1 gap-2">
        <div className="font-display text-2xl font-bold text-slate-900">{typeof kpi.value === "number" ? kpi.value.toLocaleString() : kpi.value}</div>
        {hasDelta && (
          <div className={`inline-flex items-center text-[11px] font-semibold ${up ? "text-green-700" : "text-red-700"}`}>
            {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />} {Math.abs(kpi.delta).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="mt-2">
        <Spark data={kpi.sparkline} color={up ? "#16A34A" : "#DC2626"} />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
export default function Analytics() {
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [ga4Status, setGa4Status] = useState(null);
  const [gscStatus, setGscStatus] = useState(null);
  const [liveKpis, setLiveKpis] = useState(null);
  const [liveSeries, setLiveSeries] = useState(null);
  const [liveSources, setLiveSources] = useState(null);
  const [livePages, setLivePages] = useState(null);
  const [liveQueries, setLiveQueries] = useState(null);
  const [gscKpis, setGscKpis] = useState(null);
  const [realtime, setRealtime] = useState(null);
  // Meta (Facebook + Instagram)
  const [metaStatus, setMetaStatus] = useState(null);
  const [fbKpis, setFbKpis] = useState(null);
  const [fbPosts, setFbPosts] = useState(null);
  const [igKpis, setIgKpis] = useState(null);
  const [igMedia, setIgMedia] = useState(null);

  const fetchGA4 = async () => {
    try {
      const status = await api.get("/integrations/ga4/status");
      setGa4Status(status.data);
      if (!status.data?.ok) return;
      const [k, ts, sr, tp, rt] = await Promise.all([
        api.get("/integrations/ga4/kpis", { params: { days: 30 } }),
        api.get("/integrations/ga4/timeseries", { params: { days: 30 } }),
        api.get("/integrations/ga4/top-channels", { params: { days: 30, limit: 8 } }),
        api.get("/integrations/ga4/top-pages", { params: { days: 30, limit: 15 } }),
        api.get("/integrations/ga4/realtime").catch(() => ({ data: null })),
      ]);
      // Map GA4 KPIs into the KPI card shape used by the UI
      const k4 = k.data || {};
      setLiveKpis([
        { id: "visitors",  label: "Active users", value: k4.active_users,   delta: null, sparkline: (ts.data?.rows || []).map(r => r.active_users) },
        { id: "pageviews", label: "Page views",   value: k4.page_views,     delta: null, sparkline: (ts.data?.rows || []).map(r => r.sessions) },
        { id: "session",   label: "Avg session",  value: fmtDur(k4.avg_session_seconds), delta: null, sparkline: [] },
        { id: "bounce",    label: "Bounce rate",  value: `${k4.bounce_rate_pct}%`, delta: null, sparkline: [] },
      ]);
      setLiveSeries((ts.data?.rows || []).map(r => ({
        day: new Date(r.date).toLocaleDateString([], { day: "2-digit", month: "short" }),
        visitors: r.active_users,
        pageviews: r.sessions,
      })));
      const colors = ["#F54800", "#0F172A", "#0A66C2", "#16A34A", "#9333EA", "#0EA5E9", "#FACC15", "#EC4899"];
      setLiveSources((sr.data?.rows || []).map((row, i) => ({
        name: row.channel || "Unknown",
        value: row.sessions,
        color: colors[i % colors.length],
      })));
      setLivePages((tp.data?.rows || []).map(row => ({
        path: row.path,
        title: row.title || row.path,
        views: row.views,
        avg_time: "—",
        bounce: "—",
      })));
      setRealtime(rt.data?.active_users ?? null);
    } catch (e) {
      setGa4Status((s) => s || { ok: false, configured: false, error: e?.message || String(e) });
    }
  };

  const fetchGSC = async () => {
    try {
      const status = await api.get("/integrations/gsc/status");
      setGscStatus(status.data);
      if (!status.data?.ok) return;
      const [k, q] = await Promise.all([
        api.get("/integrations/gsc/kpis", { params: { days: 28 } }),
        api.get("/integrations/gsc/top-queries", { params: { days: 28, limit: 15 } }),
      ]);
      setGscKpis(k.data || null);
      setLiveQueries((q.data?.rows || []).map((row) => ({
        q: row.query,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        position: row.position,
      })));
    } catch (e) {
      setGscStatus((s) => s || { ok: false, configured: false, error: e?.message || String(e) });
    }
  };

  const fetchMeta = async () => {
    try {
      const status = await api.get("/integrations/meta/status");
      setMetaStatus(status.data);
      if (!status.data?.ok) return;
      const [pk, pp, ik, im] = await Promise.all([
        api.get("/integrations/meta/page/kpis", { params: { days: 28 } }).catch(() => ({ data: null })),
        api.get("/integrations/meta/page/posts", { params: { limit: 6 } }).catch(() => ({ data: { rows: [] } })),
        api.get("/integrations/meta/instagram/kpis", { params: { days: 28 } }).catch(() => ({ data: null })),
        api.get("/integrations/meta/instagram/media", { params: { limit: 6 } }).catch(() => ({ data: { rows: [] } })),
      ]);
      setFbKpis(pk.data);
      setFbPosts(pp.data?.rows || []);
      setIgKpis(ik.data);
      setIgMedia(im.data?.rows || []);
    } catch (e) {
      setMetaStatus((s) => s || { ok: false, configured: false, error: e?.message || String(e) });
    }
  };

  useEffect(() => { fetchGA4(); fetchGSC(); fetchMeta(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchGA4(), fetchGSC(), fetchMeta()]);
    setLastRefresh(new Date());
    setRefreshing(false);
    toast.success("Refreshed");
  };

  const KPIS_FOR_RENDER = liveKpis || MOCK_KPIS;
  const TIMESERIES_FOR_RENDER = liveSeries || MOCK_TIMESERIES;
  const SOURCES_FOR_RENDER = liveSources || MOCK_SOURCES;
  const PAGES_FOR_RENDER = livePages || MOCK_TOP_PAGES;
  const isLive = !!(ga4Status?.ok && liveKpis);

  const totalSourceVisitors = useMemo(() => SOURCES_FOR_RENDER.reduce((s, x) => s + (x.value || 0), 0), [SOURCES_FOR_RENDER]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="analytics-page">
      <PageHeader
        eyebrow="Marketing intelligence"
        title="Website analytics"
        subtitle="Traffic, search, backlinks, and where you stand vs the top B2B studios — explained in plain English."
        actions={
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 hidden sm:block" data-testid="analytics-last-refresh">Updated {formatDateTime(lastRefresh)}</span>
            <AnalyticsAddTaskButton />
            <Button variant="outline" className="rounded-sm" onClick={refresh} disabled={refreshing} data-testid="analytics-refresh">
              <RefreshCw size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
        }
      />

      {/* Live / demo banner */}
      {isLive ? (
        <div className="card-flat p-3 mb-6 border-l-4 border-l-green-500 bg-green-50/50 flex items-start gap-2" data-testid="analytics-live-banner">
          <Sparkles size={14} className="text-green-700 mt-0.5 shrink-0" />
          <div className="text-xs text-green-900">
            <span className="font-bold">Live</span> · GA4 Property <span className="font-mono">{ga4Status?.property_id}</span>
            {realtime != null && <span className="ml-2">· {realtime} {realtime === 1 ? "user" : "users"} on the site right now</span>}
          </div>
        </div>
      ) : ga4Status?.configured && !ga4Status?.ok ? (
        <div className="card-flat p-3 mb-6 border-l-4 border-l-red-500 bg-red-50/50 flex items-start gap-2" data-testid="analytics-ga4-error">
          <AlertTriangle size={14} className="text-red-700 mt-0.5 shrink-0" />
          <div className="text-xs text-red-900">
            <span className="font-bold">GA4 connection issue:</span> {ga4Status?.error}
          </div>
        </div>
      ) : (
        <div className="card-flat p-3 mb-6 border-l-4 border-l-amber-400 bg-amber-50 flex items-start gap-2" data-testid="analytics-mock-banner">
          <Sparkles size={14} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <span className="font-bold">Demo data</span> — set GA4_SERVICE_ACCOUNT_JSON_PATH + GA4_PROPERTY_ID in backend env to go live.
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {KPIS_FOR_RENDER.map((kpi) => <KPICard key={kpi.id} kpi={kpi} />)}
      </div>

      {/* Traffic over time */}
      <div className="card-flat p-5 mb-6" data-testid="analytics-traffic-chart">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="label-overline">Last 30 days</div>
            <div className="font-display text-lg font-bold text-slate-900 mt-0.5">Traffic trend</div>
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#F54800]" /> Visitors</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-900" /> Page views</span>
          </div>
        </div>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={TIMESERIES_FOR_RENDER} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="grVis" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F54800" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#F54800" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={10} tickLine={false} />
              <YAxis stroke="#94A3B8" fontSize={10} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #E2E8F0" }} />
              <Area type="monotone" dataKey="visitors" stroke="#F54800" strokeWidth={2} fill="url(#grVis)" />
              <Area type="monotone" dataKey="pageviews" stroke="#0F172A" strokeWidth={1.5} fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI insights */}
      <div className="card-flat p-5 mb-6" data-testid="analytics-ai-insights">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-8 h-8 rounded-sm bg-[#F54800] text-white flex items-center justify-center"><Sparkles size={15} /></span>
          <div>
            <div className="label-overline">AI recommendations</div>
            <p className="text-[11px] text-slate-500 mt-0.5">Plain-English actions ranked by impact on your bottom line.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {MOCK_AI_INSIGHTS.map((ins, i) => {
            const Icon = ins.icon;
            const tone = ins.severity === "high" ? "border-l-red-500 bg-red-50/40" : ins.severity === "medium" ? "border-l-amber-400 bg-amber-50/40" : "border-l-blue-400 bg-blue-50/40";
            return (
              <div key={ins.title} className={`border-l-4 ${tone} p-3 rounded-sm`} data-testid={`ai-insight-${i}`}>
                <div className="flex items-start gap-2">
                  <Icon size={14} className="mt-0.5 text-slate-700 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-bold text-slate-900 text-sm">{ins.title}</div>
                      <Tag tone={ins.severity === "high" ? "red" : ins.severity === "medium" ? "yellow" : "blue"}>{ins.severity}</Tag>
                    </div>
                    <p className="text-xs text-slate-700 mt-1 leading-relaxed">{ins.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Sources */}
        <div className="card-flat p-5 lg:col-span-1" data-testid="analytics-sources">
          <div className="label-overline mb-1">Where they came from</div>
          <p className="text-[11px] text-slate-500 mb-3">Traffic sources, last 30 days.</p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={MOCK_SOURCES} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {MOCK_SOURCES.map((s) => <Cell key={s.name} fill={s.color} />)}
                </Pie>
                <Tooltip formatter={(v) => v.toLocaleString()} contentStyle={{ fontSize: 12, borderRadius: 2 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="space-y-1.5 mt-2">
            {SOURCES_FOR_RENDER.map((s) => {
              const pct = ((s.value / totalSourceVisitors) * 100).toFixed(1);
              return (
                <li key={s.name} className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-2 truncate"><span className="w-2 h-2 rounded-sm" style={{ background: s.color }} /><span className="text-slate-700">{s.name}</span></span>
                  <span className="font-mono text-slate-500">{pct}%</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Geo */}
        <div className="card-flat p-5 lg:col-span-2" data-testid="analytics-geo">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-overline">Where in the world</div>
              <p className="text-[11px] text-slate-500 mt-0.5">Top 10 countries by visitors.</p>
            </div>
            <Globe size={14} className="text-slate-400" />
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            {MOCK_GEO.map((g) => {
              const max = Math.max(...MOCK_GEO.map((x) => x.visitors));
              const pct = (g.visitors / max) * 100;
              return (
                <div key={g.code} className="flex items-center gap-3 text-sm" data-testid={`geo-${g.code}`}>
                  <span className="text-xl shrink-0" aria-hidden="true">{g.flag}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between">
                      <span className="text-slate-800 truncate">{g.country}</span>
                      <span className="font-mono text-[11px] text-slate-500">{g.visitors.toLocaleString()}</span>
                    </div>
                    <div className="h-1 bg-slate-100 rounded-sm overflow-hidden mt-1">
                      <div className="h-full bg-[#F54800]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top pages */}
      <div className="card-flat mb-6 overflow-hidden" data-testid="analytics-top-pages">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-overline">Top pages</div>
            <p className="text-[11px] text-slate-500 mt-0.5">Top 20 most-viewed pages on the site.</p>
          </div>
          <BarChart3 size={14} className="text-slate-400" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="label-overline px-5 py-2 font-medium">Page</th>
                <th className="label-overline px-3 py-2 font-medium text-right">Views</th>
                <th className="label-overline px-3 py-2 font-medium text-right hidden md:table-cell">Avg time</th>
                <th className="label-overline px-3 py-2 font-medium text-right hidden md:table-cell">Bounce</th>
                <th className="label-overline px-5 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {PAGES_FOR_RENDER.map((p, i) => {
                const status = PAGE_STATUS(p.views);
                return (
                  <tr key={p.path} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`top-page-${i}`}>
                    <td className="px-5 py-2 max-w-md">
                      <div className="text-slate-900 truncate font-medium">{p.title}</div>
                      <div className="text-[10px] text-slate-500 truncate font-mono">{p.path}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-900">{p.views.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-slate-600 hidden md:table-cell">{p.avg_time}</td>
                    <td className="px-3 py-2 text-right text-slate-600 hidden md:table-cell">{p.bounce}</td>
                    <td className="px-5 py-2"><Tag tone={status.tone}>{status.label}</Tag></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top queries */}
      <div className="card-flat mb-6 overflow-hidden" data-testid="analytics-top-queries">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-overline">Top search queries</div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {gscStatus?.ok && gscKpis ? (
                <>
                  Last 28 days · <b>{gscKpis.clicks.toLocaleString()}</b> clicks · <b>{gscKpis.impressions.toLocaleString()}</b> impressions · <b>{gscKpis.ctr}%</b> CTR · avg position <b>{gscKpis.position}</b>
                </>
              ) : (
                "From Google Search Console — keywords driving impressions and clicks."
              )}
            </p>
          </div>
          <div className="inline-flex items-center gap-2">
            {gscStatus?.ok ? (
              <Tag tone="green" data-testid="gsc-live-chip">live · GSC</Tag>
            ) : gscStatus?.configured === false ? null : (
              <Tag tone="yellow" data-testid="gsc-reconnect-chip" title={gscStatus?.error || ""}>reconnect needed</Tag>
            )}
            <Search size={14} className="text-slate-400" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="label-overline px-5 py-2 font-medium">Query</th>
                <th className="label-overline px-3 py-2 font-medium text-right">Impressions</th>
                <th className="label-overline px-3 py-2 font-medium text-right">Clicks</th>
                <th className="label-overline px-3 py-2 font-medium text-right">CTR</th>
                <th className="label-overline px-3 py-2 font-medium text-right">Position</th>
              </tr>
            </thead>
            <tbody>
              {(liveQueries || MOCK_TOP_QUERIES).map((q, i) => {
                const ctr = q.ctr != null ? Number(q.ctr).toFixed(1) : ((q.clicks / Math.max(1, q.impressions)) * 100).toFixed(1);
                const posTone = q.position <= 3 ? "text-green-700 font-bold" : q.position <= 10 ? "text-amber-600 font-semibold" : "text-slate-500";
                return (
                  <tr key={q.q} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`search-query-${i}`}>
                    <td className="px-5 py-2 text-slate-900 max-w-md truncate">{q.q}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600">{q.impressions.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-900">{q.clicks}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600">{ctr}%</td>
                    <td className={`px-3 py-2 text-right font-mono ${posTone}`}>{q.position.toFixed(1)}</td>
                  </tr>
                );
              })}
              {(liveQueries && liveQueries.length === 0) && (
                <tr><td colSpan={5} className="px-5 py-6 text-center text-xs text-slate-400">No GSC data for the last 28 days yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Backlinks */}
        <div className="card-flat p-5" data-testid="analytics-backlinks">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-overline">Backlinks</div>
              <p className="text-[11px] text-slate-500 mt-0.5">Sites linking to analogydesign.co.</p>
            </div>
            <Link2 size={14} className="text-slate-400" />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-slate-50 rounded-sm p-3">
              <div className="label-overline">Total</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-1">{MOCK_BACKLINKS.total}</div>
            </div>
            <div className="bg-slate-50 rounded-sm p-3">
              <div className="label-overline">Quality score</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-1">{MOCK_BACKLINKS.quality}<span className="text-sm text-slate-400 font-normal">/100</span></div>
            </div>
          </div>
          <div className="label-overline mb-2">Recent</div>
          <ul className="space-y-2">
            {MOCK_BACKLINKS.recent.map((b, i) => (
              <li key={b.domain} className="flex items-start gap-2 text-xs" data-testid={`backlink-${i}`}>
                <ExternalLink size={11} className="text-slate-400 mt-0.5 shrink-0" />
                <a
                  href={b.url || `https://${b.domain}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="min-w-0 flex-1 group hover:bg-slate-50 rounded-sm -m-1 p-1 transition-colors"
                  data-testid={`backlink-link-${i}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-[#F54800] truncate group-hover:underline">{b.domain}</span>
                    <span className="font-mono text-[10px] text-slate-500 shrink-0">DA {b.da}</span>
                  </div>
                  <div className="text-slate-500 truncate italic">&ldquo;{b.anchor}&rdquo;</div>
                  <div className="text-[10px] text-slate-400 truncate">{b.url || `https://${b.domain}`} · {formatDate(b.date)}</div>
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Competitor benchmark */}
        <div className="card-flat p-5" data-testid="analytics-competitors">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-overline">vs top B2B design studios</div>
              <p className="text-[11px] text-slate-500 mt-0.5">Visitor + backlink + ranking benchmark.</p>
            </div>
            <Trophy size={14} className="text-slate-400" />
          </div>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={MOCK_COMPETITORS} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" stroke="#94A3B8" fontSize={10} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <YAxis type="category" dataKey="name" stroke="#475569" fontSize={10} width={140} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => v.toLocaleString()} contentStyle={{ fontSize: 12, borderRadius: 2 }} />
                <Bar dataKey="visitors" radius={[0, 3, 3, 0]}>
                  {MOCK_COMPETITORS.map((c) => <Cell key={c.name} fill={c.you ? "#F54800" : "#94A3B8"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 text-[11px] text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-900">You&apos;re #4 by monthly visitors</span> — closing the gap to <span className="italic">Studio Banana</span> (54k) is a 4-5x move and most realistic in 12 months. Focus on the 12 keywords where you&apos;re already in positions 3-5.
          </div>
        </div>
      </div>

      {/* AEO / GEO */}
      <div className="card-flat p-5 mb-6" data-testid="analytics-aeo">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-overline">AI Engine Optimization (AEO + GEO)</div>
            <p className="text-[11px] text-slate-500 mt-0.5">How often you appear in ChatGPT / Perplexity / Claude / Gemini answers for your niche.</p>
          </div>
          <Bot size={14} className="text-slate-400" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { e: "ChatGPT", mentions: 2, total: 47, color: "#10A37F" },
            { e: "Perplexity", mentions: 1, total: 38, color: "#20808D" },
            { e: "Claude", mentions: 0, total: 32, color: "#D97757" },
            { e: "Gemini", mentions: 1, total: 41, color: "#4285F4" },
          ].map((a, i) => {
            const pct = (a.mentions / a.total) * 100;
            return (
              <div key={a.e} className="bg-slate-50 rounded-sm p-3" data-testid={`aeo-engine-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-900">{a.e}</span>
                  <span className="w-2 h-2 rounded-sm" style={{ background: a.color }} />
                </div>
                <div className="font-display text-xl font-bold text-slate-900 mt-1">{a.mentions}<span className="text-xs font-normal text-slate-400">/{a.total}</span></div>
                <div className="text-[10px] text-slate-500">cited in {pct.toFixed(0)}% of prompts</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-[11px] text-slate-600 leading-relaxed">
          AI engines crawl Q&amp;A-formatted pages most aggressively. <span className="font-semibold">Action</span>: add a 200-word FAQ section to your top 5 service pages — each Q starting with &ldquo;Why&rdquo;, &ldquo;How&rdquo;, &ldquo;What&rdquo; → answer in &lt;60 words. Free win.
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* Social Media — Facebook Page + Instagram Business (live via Meta) */}
      {/* ────────────────────────────────────────────────────────────────── */}
      <div className="card-flat p-5 mb-6" data-testid="analytics-meta">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="label-overline">Social media · last 28 days</div>
            <div className="font-display text-lg font-bold text-slate-900 mt-0.5">Facebook + Instagram</div>
          </div>
          {metaStatus?.ok ? (
            <Tag tone="green" data-testid="meta-live-chip">live · Graph API</Tag>
          ) : metaStatus?.configured === false ? (
            <Tag tone="yellow" data-testid="meta-not-configured">not connected</Tag>
          ) : metaStatus ? (
            <Tag tone="red" data-testid="meta-error-chip" title={metaStatus?.error || ""}>connection issue</Tag>
          ) : (
            <Tag tone="blue" data-testid="meta-loading">loading…</Tag>
          )}
        </div>

        {metaStatus?.ok ? (
          <>
            {/* KPI grid — 4 standard tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <div className="bg-slate-50 rounded-sm p-3" data-testid="meta-kpi-followers">
                <div className="flex items-center justify-between"><span className="label-overline">Page followers</span><Users size={12} className="text-slate-400" /></div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{(fbKpis?.page_followers ?? metaStatus?.page_followers ?? 0).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{metaStatus?.page_name || "Facebook Page"}</div>
              </div>
              <div className="bg-slate-50 rounded-sm p-3" data-testid="meta-kpi-ig-followers">
                <div className="flex items-center justify-between"><span className="label-overline">Instagram followers</span><Instagram size={12} className="text-slate-400" /></div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{(igKpis?.followers_count ?? metaStatus?.instagram_followers ?? 0).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">@{metaStatus?.instagram_username || "—"}</div>
              </div>
              <div className="bg-slate-50 rounded-sm p-3" data-testid="meta-kpi-reach">
                <div className="flex items-center justify-between"><span className="label-overline">IG reach (28d)</span><Eye size={12} className="text-slate-400" /></div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{(igKpis?.reach ?? 0).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">profile views {(igKpis?.profile_views ?? 0).toLocaleString()}</div>
              </div>
              <div className="bg-slate-50 rounded-sm p-3" data-testid="meta-kpi-engagement">
                <div className="flex items-center justify-between"><span className="label-overline">Page engagement (28d)</span><Heart size={12} className="text-slate-400" /></div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{(fbKpis?.page_post_engagements ?? 0).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">page views {(fbKpis?.page_views_total ?? 0).toLocaleString()}</div>
              </div>
            </div>

            {/* Top posts — FB + IG side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Facebook recent posts */}
              <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="meta-fb-posts">
                <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Facebook size={14} className="text-[#0866FF]" />
                    <span className="font-semibold text-sm text-slate-900">Recent Facebook posts</span>
                  </div>
                  <span className="text-[10px] text-slate-500">{fbPosts?.length || 0} posts</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {(fbPosts || []).slice(0, 6).map((p, i) => (
                    <li key={p.id} className="px-4 py-3 hover:bg-slate-50" data-testid={`fb-post-${i}`}>
                      <div className="flex items-start gap-3">
                        {p.image ? (
                          <img src={p.image} alt="" className="w-12 h-12 rounded-sm object-cover shrink-0 bg-slate-100" />
                        ) : (
                          <div className="w-12 h-12 rounded-sm bg-slate-100 shrink-0 flex items-center justify-center"><Facebook size={16} className="text-slate-400" /></div>
                        )}
                        <div className="min-w-0 flex-1">
                          <a href={p.permalink_url} target="_blank" rel="noreferrer" className="text-xs text-slate-900 hover:text-[#F54800] line-clamp-2 leading-snug">
                            {p.message || "(no caption)"}
                          </a>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 font-mono">
                            <span className="inline-flex items-center gap-1"><Heart size={9} /> {p.engagements}</span>
                            <span className="inline-flex items-center gap-1"><MousePointerClick size={9} /> {p.clicks}</span>
                            <span>{p.created_time ? formatDate(p.created_time) : ""}</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  {(!fbPosts || fbPosts.length === 0) && (
                    <li className="px-4 py-6 text-center text-xs text-slate-400">No recent posts</li>
                  )}
                </ul>
              </div>

              {/* Instagram recent media */}
              <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="meta-ig-media">
                <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Instagram size={14} className="text-[#E1306C]" />
                    <span className="font-semibold text-sm text-slate-900">Recent Instagram posts</span>
                  </div>
                  <span className="text-[10px] text-slate-500">{igMedia?.length || 0} posts</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {(igMedia || []).slice(0, 6).map((m, i) => (
                    <li key={m.id} className="px-4 py-3 hover:bg-slate-50" data-testid={`ig-media-${i}`}>
                      <div className="flex items-start gap-3">
                        {m.thumbnail ? (
                          <img src={m.thumbnail} alt="" className="w-12 h-12 rounded-sm object-cover shrink-0 bg-slate-100" />
                        ) : (
                          <div className="w-12 h-12 rounded-sm bg-slate-100 shrink-0 flex items-center justify-center"><Instagram size={16} className="text-slate-400" /></div>
                        )}
                        <div className="min-w-0 flex-1">
                          <a href={m.permalink} target="_blank" rel="noreferrer" className="text-xs text-slate-900 hover:text-[#F54800] line-clamp-2 leading-snug">
                            {m.caption || "(no caption)"}
                          </a>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 font-mono">
                            <span className="inline-flex items-center gap-1"><Heart size={9} /> {(m.likes || 0).toLocaleString()}</span>
                            <span className="inline-flex items-center gap-1"><MessageCircle size={9} /> {(m.comments || 0).toLocaleString()}</span>
                            <span className="inline-flex items-center gap-1"><Eye size={9} /> {(m.reach || 0).toLocaleString()}</span>
                            <span>{m.timestamp ? formatDate(m.timestamp) : ""}</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  {(!igMedia || igMedia.length === 0) && (
                    <li className="px-4 py-6 text-center text-xs text-slate-400">No recent media</li>
                  )}
                </ul>
              </div>
            </div>
          </>
        ) : (
          <div className="text-xs text-slate-500 px-2 py-6 text-center" data-testid="meta-empty">
            {metaStatus?.error
              ? <>Meta API issue: <span className="font-mono text-slate-700">{metaStatus.error}</span></>
              : "Configure META_APP_ID + META_LONG_LIVED_TOKEN + META_PAGE_ID in backend env to go live."}
          </div>
        )}
      </div>

      <div className="text-[10px] text-slate-400 text-center pb-4">
        Numbers are illustrative. Connect Google Analytics 4 + Search Console to make this dashboard live.
      </div>
    </div>
  );
}
